"use client";

import { useCallback, useState } from "react";
import type { RemoveBgJob } from "@/types/removebg";
import { downloadRemoveBgVariant } from "@/utils/removebg/api";
import { showToast } from "@/components/ui/Toast";

interface RemoveBgDownloadProps {
  job: RemoveBgJob;
}

export default function RemoveBgDownload({ job }: RemoveBgDownloadProps) {
  const [isDownloadingHd, setIsDownloadingHd] = useState(false);
  const [isDownloadingPreview, setIsDownloadingPreview] = useState(false);

  const handleDownloadPreview = useCallback(async () => {
    setIsDownloadingPreview(true);
    try {
      const blob = await downloadRemoveBgVariant(job.job_id, "preview");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${job.job_id}-preview.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast("Download do preview concluído com sucesso!", "success");
    } catch (error) {
      console.error(error);
      showToast("Não foi possível baixar o preview.", "error");
    } finally {
      setIsDownloadingPreview(false);
    }
  }, [job.job_id]);

  const handleDownloadHd = useCallback(async () => {
    setIsDownloadingHd(true);
    try {
      const blob = await downloadRemoveBgVariant(job.job_id, "hd");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${job.job_id}-hd.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast("Download concluído com sucesso!", "success");
    } catch (error) {
      console.error(error);
      showToast("Não foi possível baixar a versão HD.", "error");
    } finally {
      setIsDownloadingHd(false);
    }
  }, [job.job_id]);

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div>
        <p className="font-semibold">Downloads</p>
        <p className="text-white/60">Baixe o preview otimizado ou a versão em alta resolução.</p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleDownloadPreview}
          disabled={isDownloadingPreview}
          className="inline-flex min-w-[120px] items-center justify-center rounded-full bg-white/10 px-5 py-2 font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDownloadingPreview ? "Gerando..." : "Baixar preview"}
        </button>
        <button
          type="button"
          onClick={handleDownloadHd}
          disabled={isDownloadingHd}
          className="inline-flex min-w-[120px] items-center justify-center rounded-full bg-brand-primary px-5 py-2 font-semibold text-white transition hover:bg-brand-secondary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDownloadingHd ? "Gerando..." : "Baixar HD"}
        </button>
      </div>
    </div>
  );
}
